// The Leaflet canvas for the allocation map. Plain Leaflet, driven by refs,
// rather than a React wrapper library: one dependency instead of two, and the
// wrapper's lifecycle is exactly what would have to be worked around here
// anyway, because the layers are redrawn wholesale whenever the selection
// changes.
//
// The basemap is OpenStreetMap raster tiles: no key, no account, no metered
// cost against a payroll that is already short. Attribution is required by the
// licence and is left on the map, not stripped.
import { useEffect, useMemo, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import './allocationMap.css'
import {
  haversineKm,
  type CarPlacement,
  type LatLng,
  type MapDriver,
} from '../../utils/allocationMap'

type Props = {
  cars: CarPlacement[]
  bench: MapDriver[]
  showBench: boolean
  selectedVehicleId: string | null
  onSelectVehicle: (vehicleId: string | null) => void
}

// Sandton, as a last resort when there is nothing to fit to. Head office is at
// 155 West Street, so an empty map at least opens where Fleetly is.
const FALLBACK_CENTRE: LatLng = { lat: -26.1076, lng: 28.0567 }

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function tooltipFor(d: MapDriver): string {
  const lines = [`<strong>${escapeHtml(d.driver_name)}</strong>`]
  if (d.plate) {
    const seat = d.seat_label ? `, ${escapeHtml(d.seat_label)}` : ''
    lines.push(`${escapeHtml(d.plate)}${seat}${d.is_planned ? ' (planned)' : ''}`)
  } else {
    lines.push('On the bench, no seat this run')
  }
  if (d.suburb_or_city) lines.push(escapeHtml(d.suburb_or_city))
  if (d.manager_name) lines.push(`Manager: ${escapeHtml(d.manager_name)}`)
  return lines.join('<br>')
}

function pinIcon(label: string, classes: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div class="fleetly-pin ${classes}">${escapeHtml(label)}</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  })
}

export function AllocationMap({
  cars,
  bench,
  showBench,
  selectedVehicleId,
  onSelectVehicle,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layerRef = useRef<L.LayerGroup | null>(null)
  // Fit to everything once. After that the operator's pan and zoom is theirs;
  // only an explicit car selection moves the view.
  const fittedRef = useRef(false)

  // Create the map once, and only once. Leaflet mutates the container, so a
  // second initialisation on the same node throws.
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return
    const map = L.map(containerRef.current, {
      center: [FALLBACK_CENTRE.lat, FALLBACK_CENTRE.lng],
      zoom: 10,
      scrollWheelZoom: true,
      attributionControl: true,
    })
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map)
    layerRef.current = L.layerGroup().addTo(map)
    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
      layerRef.current = null
    }
  }, [])

  const seatedDrivers = useMemo(() => cars.flatMap((c) => c.drivers), [cars])

  // Redraw every overlay whenever the data or the selection changes. Fourteen
  // cars and forty-odd pins is nowhere near enough to need incremental updates,
  // and a full redraw cannot leave a stale marker behind.
  useEffect(() => {
    const map = mapRef.current
    const layer = layerRef.current
    if (!map || !layer) return
    layer.clearLayers()

    const selected = selectedVehicleId
      ? (cars.find((c) => c.vehicle_id === selectedVehicleId) ?? null)
      : null

    for (const d of seatedDrivers) {
      const isSelected = selected !== null && d.vehicle_id === selected.vehicle_id
      const classes = [
        isSelected ? 'fleetly-pin--selected' : '',
        d.is_planned ? 'fleetly-pin--planned' : '',
      ]
        .filter(Boolean)
        .join(' ')
      const marker = L.marker([d.lat, d.lng], {
        icon: pinIcon(String(d.shift_index ?? '?'), classes),
        title: d.driver_name,
        zIndexOffset: isSelected ? 1000 : 0,
      })
      marker.bindTooltip(tooltipFor(d), { direction: 'top', offset: [0, -12] })
      marker.on('click', () => onSelectVehicle(d.vehicle_id))
      layer.addLayer(marker)
    }

    if (showBench) {
      for (const d of bench) {
        const marker = L.marker([d.lat, d.lng], {
          icon: pinIcon('B', 'fleetly-pin--bench'),
          title: d.driver_name,
        })
        marker.bindTooltip(tooltipFor(d), { direction: 'top', offset: [0, -12] })
        layer.addLayer(marker)
      }
    }

    // The selected crew: every pair drawn faintly, the widest pair drawn solid
    // in Fleetly crimson and labelled. The widest pair IS the spread figure in
    // the list beside the map, so the number and the picture cannot disagree.
    if (selected && selected.drivers.length >= 2) {
      let widest: { a: MapDriver; b: MapDriver; km: number } | null = null
      for (let i = 0; i < selected.drivers.length; i++) {
        for (let j = i + 1; j < selected.drivers.length; j++) {
          const a = selected.drivers[i]!
          const b = selected.drivers[j]!
          const km = haversineKm(a, b)
          layer.addLayer(
            L.polyline(
              [
                [a.lat, a.lng],
                [b.lat, b.lng],
              ],
              { color: '#C30C44', weight: 1, opacity: 0.25, interactive: false },
            ),
          )
          if (!widest || km > widest.km) widest = { a, b, km }
        }
      }
      if (widest) {
        const line = L.polyline(
          [
            [widest.a.lat, widest.a.lng],
            [widest.b.lat, widest.b.lng],
          ],
          { color: '#C30C44', weight: 3, opacity: 0.9 },
        )
        line.bindTooltip(
          `Widest handover on ${escapeHtml(selected.plate)}: ${widest.km.toFixed(1)} km, ` +
            `${escapeHtml(widest.a.driver_name)} to ${escapeHtml(widest.b.driver_name)}`,
          { sticky: true },
        )
        layer.addLayer(line)
      }
    }
  }, [cars, bench, showBench, selectedVehicleId, seatedDrivers, onSelectVehicle])

  // Fit the view: to the selected crew when one is chosen, otherwise once to
  // everything on the map.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const selected = selectedVehicleId
      ? (cars.find((c) => c.vehicle_id === selectedVehicleId) ?? null)
      : null

    if (selected && selected.drivers.length > 0) {
      const bounds = L.latLngBounds(selected.drivers.map((d) => [d.lat, d.lng] as [number, number]))
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 14 })
      return
    }
    if (fittedRef.current) return
    const all = [...seatedDrivers, ...bench]
    if (all.length === 0) return
    map.fitBounds(L.latLngBounds(all.map((d) => [d.lat, d.lng] as [number, number])), {
      padding: [40, 40],
      maxZoom: 12,
    })
    fittedRef.current = true
  }, [cars, bench, seatedDrivers, selectedVehicleId])

  // Leaflet measures its container on creation. Inside a tab that mounts hidden
  // or resizes, that measurement is wrong until it is told to look again.
  useEffect(() => {
    const map = mapRef.current
    const el = containerRef.current
    if (!map || !el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => map.invalidateSize())
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return <div ref={containerRef} className="fleetly-map rounded-lg border border-border" />
}
